import React, { useState, useEffect } from 'react';
import { Card, ProgressBar, Button, Spinner, Alert, Table, Pagination } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';


const Progress = ({isEditable = true,userId}) => {
  // const [user, setUser] = useState(null);
  const [mockTests, setMockTests] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attemptPage, setAttemptPage] = useState({});
  const attemptsPerPage = 5;
  const navigate = useNavigate();

  const { user, viewMode } = useAuth();
    const { bearerAuth } = useApi();
  useEffect(() => {
  
    const fetchUserAndTests = async () => {
      try {
        setLoading(true);
        setError('');


        // Get all available mock tests
        const courseIds = Array.isArray(user?.CourseId)
          ? user.CourseId.join(',')
          : (user?.CourseId ?? '');
        if (!courseIds) {
          setMockTests([]);
        } else {
          const testsResponse = await bearerAuth(viewMode==='user'
            ? `mock-tests/${courseIds}`
            : `driving-school/mock-tests/${courseIds}`);

          if (!testsResponse.data.status) {
            // If API returns false, still proceed with empty tests
            setMockTests([]);
          } else {

            setMockTests(testsResponse?.data?.data || []);
          }
        }

        // Get user's test results (use effectiveUser.id)
        const resultsResponse = await bearerAuth(`mock-tests-results`);

        if (resultsResponse.data.status) {
          // Sort results by date (newest first)
          const sortedResults = (resultsResponse.data.data || []).sort((a, b) =>
            new Date(b.taken_on) - new Date(a.taken_on)
          );
          setTestResults(sortedResults);
        } else {
          // no results is ok
          setTestResults([]);
        }
      } catch (err) {
        console.error('Error:', err);
        setError(err.message || 'An error occurred while loading data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // re-run when parent passes different userId

  const handleTestClick = (testId, testName) => {
    // BLOCK action for school view or when not editable
    if (!isEditable || viewMode === 'school') {
      // optional: show non-intrusive message
      Swal.fire({
        icon: 'info',
        title: 'Read-only',
        text: 'School users can only view progress. Test taking is disabled here.',
        toast: true,
        position: 'top-end',
        timer: 2500,
        showConfirmButton: false
      });
      return;
    }

    // else navigate to test page for actual user
    navigate('/course-mock-test', {
      state: {
        testId: testId,
        courseName: testName,
        username: user?.username || "User"
      }
    });
  };

  const calculateProgress = (test) => {
    // Find all results for this test
    const testResultsForTest = testResults.filter(result => String(result.test_id) === String(test.id));
    if (testResultsForTest.length === 0) {
      return {
        attempts: 0,
        highestScore: 0,
        currentScore: 0,
        progressPercentage: 0,
        isComplete: false,
        lastAttemptDate: null
      };
    }

    const scores = testResultsForTest.map(result => parseInt(result.score || 0, 10));
    const highestScore = Math.max(...scores);
    const latest = testResultsForTest[0];
    const currentScore = parseInt(latest?.score || 0, 10); // newest assuming sorted
    const totalQuestions = parseInt(latest?.total_questions || 0, 10);
    const passingScore = parseInt(test.passing_score || 3, 10);
    const currentPercentage = Number.isFinite(latest?.percentage)
      ? Math.round(latest.percentage)
      : (totalQuestions > 0 ? Math.round((currentScore / totalQuestions) * 100) : 0);
    const highestPercentage = totalQuestions > 0
      ? Math.round((highestScore / totalQuestions) * 100)
      : Math.min(100, Math.round((highestScore / passingScore) * 100));

    return {
      attempts: testResultsForTest.length,
      highestScore,
      currentScore,
      totalQuestions,
      currentPercentage,
      progressPercentage: Math.min(100, currentPercentage),
      isComplete: currentScore >= passingScore,
      lastAttemptDate: latest?.taken_on || null,
      highestPercentage,
      attemptsList: testResultsForTest
    };
  };

  const getAttemptPage = (testId) => attemptPage[testId] || 1;
  const setAttemptPageFor = (testId, page) => {
    setAttemptPage(prev => ({ ...prev, [testId]: page }));
  };

  // Render
  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mt-3">
        {error}
      </Alert>
    );
  }

  return (
    <div className="container py-4">
      <h2 className="mb-4">PROGRESS</h2>

      {user?.CourseName && (
        <h5 className="mb-4">Selected Course: {user.CourseName}</h5>
      )}

      {mockTests.length === 0 ? (
        <Card className="mb-4">
          <Card.Body>
            <p>No mock tests available for this course yet.</p>
          </Card.Body>
        </Card>
      ) : (
       mockTests && mockTests.map((test) => {
          const progress = calculateProgress(test);
          const isNewAttempt = progress.attempts === 0;
          const shouldShowRetake = progress.attempts > 0 && !progress.isComplete;
          const shouldShowCompleted = progress.isComplete;

          // Determine button label & whether it should be clickable
          const buttonDisabled = !isEditable || viewMode === 'school';
          const buttonLabel = isNewAttempt ? 'Take Test' : (shouldShowRetake ? 'Retake Test' : 'View Test');

          return (
            <Card key={test.id} className="mb-4 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="mb-0">{test.title || `Test ${test.id}`}</h4>
                  <span className={`badge ${progress.isComplete ? 'bg-success' : 'bg-warning'}`}>
                    {progress.isComplete ? 'Completed' : 'In Progress'}
                  </span>
                </div>

                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Progress</span>
                    <span>{progress.progressPercentage}%</span>
                  </div>
                  <ProgressBar
                    now={progress.progressPercentage}
                    variant={progress.isComplete ? 'success' : 'info'}
                    animated={!progress.isComplete}
                  />
                </div>

                <div className="row mb-3">
                  <div className="col-md-4">
                    <p><strong>Attempts:</strong> {progress.attempts}</p>
                  </div>
                  <div className="col-md-4">
                    <p>
                      <strong>Highest Score:</strong> {progress.highestScore}
                      {progress.totalQuestions > 0 ? `/${progress.totalQuestions} (${progress.highestPercentage}%)` : ''}
                    </p>
                  </div>
                  <div className="col-md-4">
                    <p>
                      <strong>Last Score:</strong> {progress.currentScore}
                      {progress.totalQuestions > 0 ? `/${progress.totalQuestions} (${progress.currentPercentage}%)` : ''}
                    </p>
                  </div>
                </div>

                {progress.lastAttemptDate && (
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <p><strong>Last Attempt:</strong> {new Date(progress.lastAttemptDate).toLocaleString()}</p>
                    </div>
                  </div>
                )}

                {progress.attempts > 0 && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">All Attempts</h6>
                      <small className="text-muted">Showing newest first</small>
                    </div>
                    <Table bordered size="sm" className="mb-2">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '10%' }}>#</th>
                          <th style={{ width: '40%' }}>Attempt Date</th>
                          <th style={{ width: '25%' }}>Score</th>
                          <th style={{ width: '25%' }}>Percent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {progress.attemptsList
                          .slice((getAttemptPage(test.id) - 1) * attemptsPerPage, getAttemptPage(test.id) * attemptsPerPage)
                          .map((attempt, idx) => {
                            const totalQ = Number(attempt.total_questions) || 0;
                            const score = Number(attempt.score) || 0;
                            const pct = Number.isFinite(attempt.percentage)
                              ? Math.round(attempt.percentage)
                              : (totalQ > 0 ? Math.round((score / totalQ) * 100) : 0);
                            const rowIndex = (getAttemptPage(test.id) - 1) * attemptsPerPage + idx + 1;
                            return (
                              <tr key={attempt.id || rowIndex}>
                                <td>{rowIndex}</td>
                                <td>{attempt.taken_on ? new Date(attempt.taken_on).toLocaleString() : 'N/A'}</td>
                                <td>{totalQ > 0 ? `${score}/${totalQ}` : score}</td>
                                <td>{totalQ > 0 ? `${pct}%` : 'N/A'}</td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </Table>

                    {progress.attempts > attemptsPerPage && (
                      <Pagination className="mb-0">
                        <Pagination.Prev
                          onClick={() => setAttemptPageFor(test.id, Math.max(1, getAttemptPage(test.id) - 1))}
                          disabled={getAttemptPage(test.id) === 1}
                        />
                        {Array.from({ length: Math.ceil(progress.attempts / attemptsPerPage) }).map((_, i) => (
                          <Pagination.Item
                            key={i + 1}
                            active={getAttemptPage(test.id) === i + 1}
                            onClick={() => setAttemptPageFor(test.id, i + 1)}
                          >
                            {i + 1}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next
                          onClick={() => setAttemptPageFor(test.id, Math.min(Math.ceil(progress.attempts / attemptsPerPage), getAttemptPage(test.id) + 1))}
                          disabled={getAttemptPage(test.id) === Math.ceil(progress.attempts / attemptsPerPage)}
                        />
                      </Pagination>
                    )}
                  </div>
                )}

                <div className="d-flex gap-2">
                  <Button
                    variant={progress.isComplete ? 'success' : 'primary'}
                    onClick={() => handleTestClick(test.id, test.title || `Test ${test.id}`)}
                    disabled={buttonDisabled}
                    title={buttonDisabled ? 'Read-only: test taking disabled for school view' : ''}
                  >
                    {buttonLabel}
                  </Button>

                  {shouldShowCompleted && (
                    <Button variant="outline-success" disabled>
                      Completed
                    </Button>
                  )}
                </div>

                {shouldShowCompleted && (
                  <div className="mt-3">
                    <Alert variant="success" className="mb-0">
                      Congratulations! You've completed this test with a score of {progress.currentScore}.
                    </Alert>
                  </div>
                )}
              </Card.Body>
            </Card>
          );
        })
      )}
    </div>
  );
};

export default Progress;
